import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AgeGateModal = ({ open, onOpenChange, onVerified }) => {
  const { user } = useApp();
  const [dob, setDob] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!dob) {
      toast.error('Please enter your date of birth');
      return;
    }
    if (!confirmed) {
      toast.error('Please confirm you are over 18');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}/verify-age`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_of_birth: dob,
          confirmed_over_18: confirmed
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success('Age verified successfully');
        onVerified?.();
        onOpenChange(false);
      } else {
        toast.error(data.detail || 'Age verification failed');
      }
    } catch {
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-minted-600" />
            <DialogTitle className="text-lg">Age Verification Required</DialogTitle>
          </div>
          <DialogDescription>
            This section contains items for adults only. Please verify your age to continue.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-3">
          <div>
            <Label className="text-sm font-medium text-gray-700">Date of Birth</Label>
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="mt-1"
              max={new Date().toISOString().split('T')[0]}
              data-testid="age-gate-dob-input"
            />
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border">
            <Checkbox
              id="age-confirm"
              checked={confirmed}
              onCheckedChange={setConfirmed}
              data-testid="age-gate-confirm-checkbox"
            />
            <label htmlFor="age-confirm" className="text-sm text-gray-700 leading-snug cursor-pointer">
              I confirm that I am <span className="font-semibold">18 years of age or older</span> and wish to view age-restricted content.
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-minted-600 hover:bg-minted-700"
            onClick={handleVerify}
            disabled={loading || !dob || !confirmed}
            data-testid="age-gate-verify-btn"
          >
            {loading ? 'Verifying...' : 'Verify Age'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgeGateModal;
